import React, { useEffect, useRef, useState } from "react";
import { formatDuration } from "./chatUtils";

// =====================================================
// VOICE PLAYER
// Self-contained playback UI for an audio message: play/pause, a seekable
// progress bar and an elapsed / total time readout. Uses a single <audio>
// element it owns; nothing about playback leaks into the parent. The known
// duration (from the upload) is shown immediately so the bubble isn't blank
// while metadata loads on slow connections.
// =====================================================

function VoicePlayer({ src, duration = 0, mine = false }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(duration || 0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => setCurrent(audio.currentTime);
    const onMeta = () => {
      // Some browsers report Infinity for streamed webm until seeked; keep the
      // server-provided duration in that case.
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setTotal(audio.duration);
      }
    };
    const onEnd = () => {
      setPlaying(false);
      setCurrent(0);
    };

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnd);
    };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().then(
        () => setPlaying(true),
        () => setPlaying(false)
      );
    }
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !total) return;
    const pct = Number(e.target.value);
    audio.currentTime = (pct / 100) * total;
    setCurrent(audio.currentTime);
  };

  const pct = total ? Math.min(100, (current / total) * 100) : 0;

  return (
    <div className={`voice-player ${mine ? "voice-mine" : "voice-theirs"}`}>
      <audio ref={audioRef} src={src} preload="metadata" />
      <button
        type="button"
        className="voice-play-btn"
        onClick={toggle}
        aria-label={playing ? "Pause voice message" : "Play voice message"}
      >
        {playing ? "⏸" : "▶"}
      </button>

      <div className="voice-track">
        <input
          type="range"
          min="0"
          max="100"
          value={pct}
          onChange={seek}
          // WebM from MediaRecorder can report duration Infinity until it is
          // fully buffered. We fall back to the duration stored with the
          // message; if even that is missing there is nothing to seek against,
          // so the control is disabled rather than silently inert.
          disabled={!total}
          className="voice-range"
          style={{ "--voice-pct": `${pct}%` }}
          aria-label="Seek"
        />
      </div>

      <span className="voice-time">
        {formatDuration(playing || current ? current : total)}
      </span>
    </div>
  );
}

export default React.memo(VoicePlayer);
